import { useState, useMemo } from "react";
import { format } from "date-fns";
import { Search, X, Settings, Eye, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDisputesList } from "@/hooks/useDisputesData";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { toast } from "sonner";
import type { DisputeUI } from "@/types/ui/disputes";
import { 
  CheckCircle2,
  Clock,
  MessageSquare,
  FileText,
  Sparkles,
  Bot,
} from "lucide-react";
import { ConsolidatedActivityTimeline } from "@/components/collections/consolidated-activity-timeline";
import type { CollectionActivity, CollectionCommunication, CaseNote } from "@/types/collections";

const DEFAULT_DISPUTE_TAB_COLUMNS: ColumnDefinition[] = [
  { id: 'disputeId', label: 'Dispute ID', visible: true, order: 0 },
  { id: 'customer', label: 'Customer', visible: true, order: 1 },
  { id: 'type', label: 'Type', visible: true, order: 2 },
  { id: 'amount', label: 'Amount', visible: true, order: 3 },
  { id: 'status', label: 'Status', visible: true, order: 4 },
  { id: 'priority', label: 'Priority', visible: true, order: 5 },
  { id: 'workflowProgress', label: 'Workflow Progress', visible: true, order: 6 },
  { id: 'aiScore', label: 'AI Score', visible: true, order: 7 },
  { id: 'assignedTo', label: 'Assigned To', visible: true, order: 8 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 9 },
  { id: 'createdDate', label: 'Created Date', visible: false, order: 10 },
  { id: 'workflow', label: 'Workflow', visible: false, order: 11 },
  { id: 'description', label: 'Description', visible: false, order: 12 },
];

interface DisputesTabProps {
  customerId: string;
  customerName: string;
  isConsolidatedView?: boolean;
}

const statusColors = {
  "New": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "Under Review": "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300",
  "In Progress": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Escalated": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
  "Resolved": "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
  "Closed": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
};

const priorityColors = {
  "Low": "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300",
  "Medium": "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
  "High": "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
  "Critical": "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
};

// Mock activity data for detail view
const mockActivities: CollectionActivity[] = [
  {
    id: "1",
    caseId: "CASE-001",
    type: "status_change",
    description: "Status changed from 'New' to 'In Progress'",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00Z"),
  },
  {
    id: "2",
    caseId: "CASE-001",
    type: "note",
    description: "Assigned to John Smith by AI Agent",
    createdBy: "System",
    createdAt: new Date("2024-01-15T14:15:00Z"),
  },
  {
    id: "3",
    caseId: "CASE-001",
    type: "status_change",
    description: "Priority changed from 'Medium' to 'High'",
    createdBy: "System",
    createdAt: new Date("2024-01-15T09:00:00Z"),
  },
];

const mockCommunications: CollectionCommunication[] = [
  {
    id: "1",
    caseId: "CASE-001",
    type: "email",
    direction: "outbound",
    subject: "Re: Dispute #DSP-2024-001",
    content: "Thank you for bringing this to our attention. We are currently reviewing...",
    sentBy: "support@company.com",
    sentAt: new Date("2024-01-16T11:00:00Z"),
    status: "sent",
  },
  {
    id: "2",
    caseId: "CASE-001",
    type: "email",
    direction: "inbound",
    subject: "Dispute regarding Invoice #INV-2024-001",
    content: "I would like to dispute the charges on invoice INV-2024-001...",
    sentBy: "customer@example.com",
    sentAt: new Date("2024-01-15T16:30:00Z"),
    status: "delivered",
  },
];

const mockNotes: CaseNote[] = [
  {
    id: "1",
    caseId: "CASE-001",
    content: "Contacted customer to verify service dates. Awaiting response.",
    createdBy: "John Smith",
    createdAt: new Date("2024-01-16T10:30:00Z"),
  },
  {
    id: "2",
    caseId: "CASE-001",
    content: "Dispute automatically categorized as 'Billing Error' with 85% confidence.",
    createdBy: "AI Agent",
    createdAt: new Date("2024-01-15T14:15:00Z"),
  },
];

// Mock documents for detail view
const documents = [
  { name: "Original Invoice.pdf", size: "245 KB", date: "2024-01-15" },
  { name: "Service Agreement.pdf", size: "1.2 MB", date: "2024-01-15" },
  { name: "Customer Email.msg", size: "45 KB", date: "2024-01-16" },
];

export function DisputesTab({ customerId, customerName, isConsolidatedView = false }: DisputesTabProps) {
  const customerIdNum = parseInt(customerId);
  const { data: allDisputes = [], isLoading } = useDisputesList();
  
  // Filter disputes for this customer
  const customerDisputes = useMemo(() => {
    return allDisputes.filter(dispute => dispute.customerId === customerIdNum);
  }, [allDisputes, customerIdNum]);
  
  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectedDispute, setSelectedDispute] = useState<DisputeUI | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  
  // Column preferences
  const { columns, savePreferences, resetToDefaults } = useScreenColumnPreferences('customer-detail-disputes', DEFAULT_DISPUTE_TAB_COLUMNS);
  
  // Filter disputes based on status, priority, and search
  const filteredDisputes = useMemo(() => {
    let filtered = customerDisputes;

    // Filter by status dropdown
    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }

    // Filter by priority dropdown
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(d => d.priority === priorityFilter);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.id.toLowerCase().includes(query) ||
        d.type.toLowerCase().includes(query) ||
        d.description.toLowerCase().includes(query) ||
        d.amount.toString().includes(query)
      );
    }

    // Sort by created date descending (newest first)
    filtered = [...filtered].sort((a, b) => 
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );

    return filtered;
  }, [customerDisputes, statusFilter, priorityFilter, searchQuery]);
  
  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return columns
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order);
  }, [columns]);
  
  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(filteredDisputes.map(d => d.id)));
    } else {
      setSelectedRows(new Set());
    }
  };
  
  const handleSelectRow = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };
  
  const isAllSelected = filteredDisputes.length > 0 && selectedRows.size === filteredDisputes.length;
  const isSomeSelected = selectedRows.size > 0 && selectedRows.size < filteredDisputes.length;
  
  // Action handlers
  const handleViewDetails = (dispute: DisputeUI) => {
    setSelectedDispute(dispute);
    setDetailSheetOpen(true);
  };
  
  const handleClearSelection = () => {
    setSelectedRows(new Set());
  };
  
  // Helper function to render table cell content based on column ID
  const renderTableCell = (columnId: string, dispute: DisputeUI) => {
    switch (columnId) {
      case 'companyId':
        // In consolidated view, simulate different company IDs
        // In real implementation, this would come from the API
        return `COMP-${Math.floor(Math.random() * 3) + 1}`;
      case 'disputeId':
        return (
          <button
            onClick={() => handleViewDetails(dispute)}
            className="text-primary hover:underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded cursor-pointer transition-colors font-medium"
          >
            {dispute.id}
          </button>
        );
      case 'customer':
        return dispute.customer;
      case 'type':
        return dispute.type;
      case 'amount':
        return `£${dispute.amount.toLocaleString()}`;
      case 'status':
        return (
          <Badge className={statusColors[dispute.status as keyof typeof statusColors]}>
            {dispute.status}
          </Badge>
        );
      case 'priority':
        return (
          <Badge className={priorityColors[dispute.priority as keyof typeof priorityColors]}>
            {dispute.priority}
          </Badge>
        );
      case 'workflowProgress':
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[100px]">
              <div
                className="bg-primary h-2 rounded-full"
                style={{ width: `${(dispute.currentLevel / dispute.totalLevels) * 100}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {dispute.currentLevel}/{dispute.totalLevels}
            </span>
          </div>
        );
      case 'aiScore':
        return dispute.aiScore !== null ? (
          <div className="flex items-center gap-1">
            <Bot className="h-3 w-3 text-purple-500" />
            <span className="text-sm font-medium">{dispute.aiScore}%</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      case 'assignedTo':
        return dispute.assignedTo;
      case 'dueDate':
        return dispute.dueDate ? format(new Date(dispute.dueDate), "MMM dd, yyyy") : "—";
      case 'createdDate':
        return format(new Date(dispute.createdDate), "MMM dd, yyyy");
      case 'workflow':
        return dispute.workflow;
      case 'description':
        return (
          <span className="truncate max-w-[200px] block" title={dispute.description}>
            {dispute.description}
          </span>
        );
      default:
        return null;
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading disputes...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Dispute Records ({filteredDisputes.length})</CardTitle>
              <CardDescription>
                Customer disputes for {customerName}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      aria-label="Configure Columns"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Configure Columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        
        {/* Search Bar and Filters */}
        <div className="px-6 pb-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by dispute ID, type, description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="Under Review">Under Review</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Escalated">Escalated</SelectItem>
                <SelectItem value="Resolved">Resolved</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all disputes"
                    className={isSomeSelected ? "data-[state=checked]:bg-primary/50" : ""}
                  />
                </TableHead>
                {isConsolidatedView && (
                  <TableHead>Company ID</TableHead>
                )}
                {visibleColumns.map((column) => (
                  <TableHead key={column.id}>
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDisputes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 1} className="text-center text-muted-foreground py-8">
                    {searchQuery || statusFilter !== 'all' || priorityFilter !== 'all'
                      ? 'No disputes found matching your criteria'
                      : 'No disputes recorded for this customer'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredDisputes.map((dispute) => {
                  const isSelected = selectedRows.has(dispute.id);
                  return (
                    <TableRow 
                      key={dispute.id}
                      className={isSelected ? "bg-muted/50" : ""}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => handleSelectRow(dispute.id, checked as boolean)}
                          aria-label={`Select dispute ${dispute.id}`}
                        />
                      </TableCell>
                      {isConsolidatedView && (
                        <TableCell>{renderTableCell('companyId', dispute)}</TableCell>
                      )}
                      {visibleColumns.map((column) => (
                        <TableCell key={column.id}>
                          {renderTableCell(column.id, dispute)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onApply={savePreferences}
        onReset={resetToDefaults}
      />
      
      {/* Dispute Details Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto px-6">
          {selectedDispute && (
            <>
              <SheetHeader className="px-0">
                <SheetTitle>Dispute Details - {selectedDispute.id}</SheetTitle>
                <SheetDescription>
                  View dispute information
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="overview" className="mt-6">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="workflow">Workflow</TabsTrigger>
                  <TabsTrigger value="activity">Activity</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="ai-insights">AI Insights</TabsTrigger>
                </TabsList>

                {/* Tab 1: Overview */}
                <TabsContent value="overview" className="space-y-4">
                  {/* TEST BUTTON */}
                  <Button 
                    onClick={() => alert('TEST BUTTON WORKS IN DISPUTES TAB!')}
                    className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-4"
                  >
                    🔴 TEST BUTTON - CLICK ME 🔴
                  </Button>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Customer</Label>
                      <Input value={selectedDispute.customer} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Dispute Type</Label>
                      <Input value={selectedDispute.type} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input value={`£${selectedDispute.amount.toLocaleString()}`} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Input value={selectedDispute.status} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Input value={selectedDispute.priority} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Assigned To</Label>
                      <Input value={selectedDispute.assignedTo} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Created Date</Label>
                      <Input value={format(new Date(selectedDispute.createdDate), "MMM dd, yyyy")} readOnly />
                    </div>
                    <div className="space-y-2">
                      <Label>Due Date</Label>
                      <Input value={format(new Date(selectedDispute.dueDate), "MMM dd, yyyy")} readOnly />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={selectedDispute.description} readOnly rows={4} />
                  </div>
                  <div className="space-y-2">
                    <Label>Resolution Notes</Label>
                    <Textarea 
                      value={selectedDispute.resolutionNotes || "No resolution notes yet"}
                      readOnly
                      rows={4}
                    />
                  </div>
                </TabsContent>

                {/* Tab 2: Workflow */}
                <TabsContent value="workflow" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Active Workflow</CardTitle>
                      <CardDescription>{selectedDispute.workflow}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 mb-6">
                        <span className="text-sm font-medium">Current Level:</span>
                        <Badge>{selectedDispute.currentLevel} of {selectedDispute.totalLevels}</Badge>
                      </div>

                      <div className="space-y-6">
                        {[
                          {
                            level: 1,
                            title: "Initial Review & Categorization",
                            description: "Dispute received and automatically categorized by AI",
                            completedDate: "2024-01-15",
                          },
                          {
                            level: 2,
                            title: "Investigation & Evidence Gathering",
                            description: "Collecting relevant documents and customer communications",
                            completedDate: selectedDispute.currentLevel >= 2 ? "2024-01-16" : null,
                          },
                          {
                            level: 3,
                            title: "Resolution Recommendation",
                            description: "AI generates resolution recommendation based on analysis",
                            completedDate: selectedDispute.currentLevel >= 3 ? "2024-01-17" : null,
                          },
                          {
                            level: 4,
                            title: "Final Approval & Closure",
                            description: "Management approval and customer notification",
                            completedDate: selectedDispute.currentLevel >= 4 ? "2024-01-18" : null,
                          },
                        ].map((step) => (
                          <div key={step.level} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div className={`flex h-8 w-8 items-center justify-center rounded-full ${
                                step.level <= selectedDispute.currentLevel
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground"
                              }`}>
                                {step.level < selectedDispute.currentLevel ? (
                                  <CheckCircle2 className="h-4 w-4" />
                                ) : step.level === selectedDispute.currentLevel ? (
                                  <Clock className="h-4 w-4" />
                                ) : (
                                  <span className="text-sm">{step.level}</span>
                                )}
                              </div>
                              {step.level < 4 && (
                                <div className={`w-0.5 h-12 ${
                                  step.level < selectedDispute.currentLevel
                                    ? "bg-primary"
                                    : "bg-muted"
                                }`} />
                              )}
                            </div>
                            <div className="flex-1 pb-8">
                              <h4 className="font-semibold">{step.title}</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {step.description}
                              </p>
                              {step.completedDate && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  Completed: {format(new Date(step.completedDate), "MMM dd, yyyy")}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Tab 3: Activity */}
                <TabsContent value="activity" className="space-y-4">
                  <ConsolidatedActivityTimeline
                    activities={mockActivities}
                    communications={mockCommunications}
                    notes={mockNotes}
                    isEditMode={false}
                  />
                </TabsContent>

                {/* Tab 4: Documents */}
                <TabsContent value="documents" className="space-y-4">
                  <Button className="w-full">
                    <FileText className="mr-2 h-4 w-4" />
                    Upload Document
                  </Button>
                  <div className="space-y-3">
                    {documents.map((doc, index) => (
                      <Card key={index}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <FileText className="h-8 w-8 text-muted-foreground" />
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {doc.size} • Uploaded {format(new Date(doc.date), "MMM dd, yyyy")}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>

                {/* Tab 5: AI Insights */}
                <TabsContent value="ai-insights" className="space-y-4">
                  <Card className="border-purple-200 dark:border-purple-800">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-purple-500" />
                        <CardTitle className="text-lg">AI-Powered Insights</CardTitle>
                      </div>
                      <CardDescription>
                        Automated analysis by Dispute Resolute Agent
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Confidence Score */}
                      {selectedDispute.aiScore !== null && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Confidence Score</Label>
                            <span className="text-sm font-medium">{selectedDispute.aiScore}%</span>
                          </div>
                          <Progress value={selectedDispute.aiScore} className="h-2" />
                        </div>
                      )}

                      {/* AI Recommendation */}
                      <div className="space-y-2">
                        <Label>AI Recommendation</Label>
                        <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200 dark:border-purple-800">
                          <CardContent className="pt-4">
                            <p className="text-sm">{selectedDispute.aiRecommendation}</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Similar Cases */}
                      <div className="space-y-2">
                        <Label>Similar Cases Analysis</Label>
                        <p className="text-sm text-muted-foreground">
                          Based on analysis of 47 similar disputes, 82% were resolved with partial credit.
                          Average resolution time: 12 days.
                        </p>
                      </div>

                      {/* Risk Assessment */}
                      <div className="space-y-2">
                        <Label>Risk Assessment</Label>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-amber-600 border-amber-600">
                            Medium Risk
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            Customer has good payment history
                          </span>
                        </div>
                      </div>

                      {/* Suggested Actions */}
                      <div className="space-y-2">
                        <Label>Suggested Actions</Label>
                        <ul className="mt-2 space-y-2">
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Verify service delivery dates from system logs</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Review contract terms for billing cycle</span>
                          </li>
                          <li className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                            <span>Consider 30% credit as resolution</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              <div className="mt-6 px-0 flex justify-end">
                <Button variant="outline" onClick={() => setDetailSheetOpen(false)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      
      {/* Floating Actions Bar */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedRows.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedRows.size === 1 ? "dispute" : "disputes"} selected
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    toast.info(`Bulk actions for ${selectedRows.size} dispute(s)`);
                  }}
                  className="gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View Selected
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
